import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "../constants";
import { useLanguage } from "../i18n/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

const NAV_KEYS = [
  { id: "fleet", key: "nav.fleet" },
  { id: "no-deposit", key: "nav.noDeposit" },
  { id: "how-it-works", key: "nav.how" },
  { id: "about", key: "nav.about" },
  { id: "faq", key: "nav.faq" },
  { id: "contact", key: "nav.contact" },
];

const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
        scrolled ? "glass-panel border-b border-gold/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          data-testid="navbar-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center"
          aria-label="A&A MOTORS"
        >
          <span className="flex flex-col leading-none">
            <span className="font-display text-2xl font-bold tracking-[0.08em] text-white">
              A<span className="text-gold-gradient">&amp;</span>A
            </span>
            <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.45em] text-gold">Motors</span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_KEYS.map((item) => (
            <button
              key={item.id}
              type="button"
              data-testid={`nav-link-${item.id}`}
              onClick={() => go(item.id)}
              className="text-[13px] font-medium uppercase tracking-wider text-slate-300 transition-colors hover:text-gold"
            >
              {t(item.key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            type="button"
            data-testid="navbar-book-button"
            onClick={() => go("enquiry")}
            className="hidden rounded-full bg-gold px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-[#08080A] transition-all hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] md:block"
          >
            {t("nav.book")}
          </button>
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label={open ? t("nav.close") : t("nav.menu")}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/25 text-slate-200 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="glass-panel overflow-hidden border-b border-gold/10 lg:hidden"
            aria-label="Mobile"
          >
            <div className="space-y-1 px-6 py-6">
              {NAV_KEYS.map((item, i) => (
                <motion.button
                  key={item.id}
                  type="button"
                  data-testid={`mobile-nav-link-${item.id}`}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(item.id)}
                  className="block w-full py-3 text-start font-display text-2xl font-medium text-slate-100 transition-colors hover:text-gold"
                >
                  {t(item.key)}
                </motion.button>
              ))}
              <button
                type="button"
                data-testid="mobile-book-button"
                onClick={() => go("enquiry")}
                className="mt-4 w-full rounded-full bg-gold py-3.5 text-sm font-bold uppercase tracking-widest text-[#08080A]"
              >
                {t("nav.book")}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
